import "./store.css";

import React from "react";
import { Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress } from "@material-ui/core";
import { useParams, Link } from "react-router-dom";

import { PAGE_ROUTES } from "../../utils/stateTypes";
import { SITE_URL } from "../../utils/seoData";
import SeoHead from "../general/SeoHead";
import { formatPrice } from "../../utils/generalUtils";
import productData, { DISCOUNT_CODES } from "./productData";

const styles = {
  input: {
    marginBottom: 10,
  },
  buyButton: {
    padding: "15px 30px",
    height: 54,
  },
  backButton: {
    marginBottom: 40,
  },
};


const Product = () => {
  const { id } = useParams();
  const product = productData.find((product) => product.id === id);

  let isShoppingSub = (id === "bi-monthly-menu-and-shopping-list");

  const [email, setEmail] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [discountCode, setDiscountCode] = React.useState("");
  const [discountCodeError, setDiscountCodeError] = React.useState("");
  const [activeDiscount, setActiveDiscount] = React.useState(null);

  //Set up the subscription fields
  let subName = "";
  const [subOption, setSubOption] = React.useState("");
  let selectedSubOption;
  if (isShoppingSub) {
    selectedSubOption = product.options.find((option) => option.id === subOption);
    subName = `${product.name} (${selectedSubOption ? selectedSubOption.billingFrequencyText : ""})`;
  }

  //Set up the price based on sub option or discount / sale
  let standardPrice = product.price;
  if (isShoppingSub) {
    standardPrice = selectedSubOption ? selectedSubOption.price : product.price;
    standardPrice = standardPrice * (selectedSubOption ? selectedSubOption.billingMultiplier : product.billingMultiplier);
  }
  let salePrice = product.salePrice;
  if (activeDiscount) {
    salePrice = product.salePrice ? (product.salePrice - (product.salePrice * activeDiscount)) : (standardPrice - (standardPrice * activeDiscount));
    salePrice = Math.round(salePrice * 100) / 100; //Round to 2 decimal places
  }
  let helperText = " ";
  if (activeDiscount) {
    helperText = `Discount applied: ${activeDiscount * 100}%`;
  }
  else if (discountCodeError) {
    helperText = discountCodeError;
  }

  const validateDiscountCode = React.useCallback(() => {
    const discount = DISCOUNT_CODES[discountCode.toLowerCase()];
    if (discount && (discount.type === product.type) && (!product.isSub)) {
      setActiveDiscount(discount.value);
      setDiscountCodeError("");
    }
    else {
      setDiscountCodeError("Invalid discount code");
      setActiveDiscount(null);
    }
  }, [discountCode, product]);

  //Set up the return URL based on the product
  let returnUrl = `https://ashthedietitian.co.za${PAGE_ROUTES.THANK_YOU}`;
  if (id === "meal-plan")
  {
    returnUrl = `https://ashthedietitian.co.za${PAGE_ROUTES.MEAL_PLAN_FORM}`;
  }

  
  let notifyUrl = "https://ashthedietitian.co.za/api/Store/NotifySale";
  if (isShoppingSub) {
    notifyUrl = "https://ashthedietitian.co.za/api/Store/NotifySaleShopping";
  }

  let gallery = [product.image];
  if (product.gallery) {
    gallery = [...gallery, ...product.gallery];
  }
  const [activeImage, setActiveImage] = React.useState(0);


  return (
    <main>
      <SeoHead
        title={`${product.name} | Ash The Dietitian`}
        description={product.metaDescription}
        path={PAGE_ROUTES.PRODUCT.replace(":id", product.id)}
        image={`${SITE_URL}${product.image}`}
      />

      <header className={"page-header standard-width"}>
        <h1 className={"section-head"}>{product.name}</h1>
      </header>

      <section className={"product-section standard-width"}>
        <div className={"product-gallery standard-box"}>
          {
            gallery.map((image, index) => (
              <img
                key={index}
                className={["product-img", activeImage === index ? "active" : ""].join(" ")}
                src={image}
                alt={product.name}
                onClick={() => setActiveImage(index)}
              />
            ))
          }
          {
            (gallery.length > 1) && (
              <div className={"product-gallery-dots"}>
                {
                  gallery.map((image, index) => (
                    <div
                      key={index}
                      className={["product-gallery-dot", activeImage === index ? "active" : ""].join(" ")}
                      onClick={() => setActiveImage(index)}
                    />
                  ))
                }
              </div>
            )
          }
        </div>
        <div className={"product-content"}>
          <div>
            <div className={"product-description"}>{product.description}</div>
            <div className={"product-price-cont"}>
              <h3 className={["product-price", salePrice ? "on-sale" : ""].join(" ")}>
                {product.isSub && <span className={"sub-pre"}>From</span>}
                <span className={"standard"}>{formatPrice(product.price)}</span>
                {salePrice && <span className={"sale"}>{formatPrice(salePrice)}</span>}
                {product.isSub && <span className={"sub"}>per month</span>}
              </h3>
              <TextField
                label={"Discount Code"}
                variant={"outlined"}
                color={"secondary"}
                size={"small"}
                style={styles.discountCodeInput}
                type={"text"}
                value={discountCode}
                onChange={(event) => {
                  setDiscountCode(event.target.value);
                  setDiscountCodeError("");
                }}
                error={discountCodeError !== ""}
                helperText={helperText}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    validateDiscountCode();
                  }
                }}
              />
              <Button
                variant={"outlined"}
                color={"secondary"}
                style={styles.discountCodeButton}
                onClick={validateDiscountCode}
              >
                Apply
              </Button>
            </div>
          </div>
          <form
            className={"product-content-form"}
            name={"PayFastPayNowForm"}
            // action={"https://sandbox.payfast.co.za/eng/process"}
            action={"https://www.payfast.co.za/eng/process"}
            method={"post"}
            onSubmit={() => {
              setIsLoading(true);
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: "checkout" });
            }}
          >
            {/* HIDDEN PAYFAST FIELDS */}
            <TextField
              required
              type={"hidden"}
              name={"cmd"}
              value={"_paynow"}
            />
            <TextField
              required
              type={"hidden"}
              name={"receiver"}
              pattern={"[0-9]"}
              value={"14797086"}
              // value={"10014195"} //Sandbox
            />
            <TextField
              type={"hidden"}
              name={"return_url"}
              value={returnUrl}
            />
            <TextField
              type={"hidden"}
              name={"cancel_url"}
              value={`https://ashthedietitian.co.za${PAGE_ROUTES.STORE}`}
            />
            <TextField
              type={"hidden"}
              name={"notify_url"}
              value={notifyUrl}
            />
            <TextField
              required
              type={"hidden"}
              name={"custom_str1"}
              value={product.id}
            />
            <TextField
              required
              type={"hidden"}
              name={"amount"}
              value={salePrice || standardPrice}
            />
            <TextField
              type={"hidden"}
              name={"item_name"}
              maxLength={"255"}
              value={isShoppingSub ? subName : product.name}
            />
            {
              (product.isSub) && (
                <React.Fragment>
                  <TextField
                    type={"hidden"}
                    name={"subscription_type"}
                    value={1}
                  />
                  <TextField
                    type={"hidden"}
                    name={"frequency"}
                    value={selectedSubOption ? selectedSubOption.billingFrequency : product.billingFrequency}
                  />
                  <TextField
                    type={"hidden"}
                    name={"cycles"}
                    value={0}
                  />
                </React.Fragment>
              )
            }

            <TextField
              label={"Email"}
              variant={"filled"}
              color={"secondary"}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              style={styles.input}
              type={"email"}
              name={"email_address"}
              fullWidth
              required
            />
            {
              (isShoppingSub) && (
                <FormControl
                  variant={"filled"}
                  color={"secondary"}
                  style={styles.input}
                  fullWidth
                >
                  <InputLabel required>Where do you do most of your shopping?</InputLabel>
                  <Select
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                    color={"secondary"}
                    name={"custom_str2"}
                    defaultValue={""}
                    required
                  >
                    <MenuItem
                      value={"Woolworths"}
                      style={styles.menuItem}
                    >
                      Woolworths
                    </MenuItem>
                    <MenuItem
                      value={"Checkers"}
                      style={styles.menuItem}
                    >
                      Checkers
                    </MenuItem>
                  </Select>
                </FormControl>
              )
            }
            {
              (isShoppingSub) && (
                <FormControl
                  required
                  variant={"filled"}
                  color={"secondary"}
                  style={styles.input}
                  fullWidth
                >
                  <InputLabel required>Select your subscription</InputLabel>
                  <Select
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                    color={"secondary"}
                    value={subOption}
                    onChange={(event) => setSubOption(event.target.value)}
                    required
                  >
                    {
                      product.options.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          style={styles.menuItem}
                        >
                          {option.billingFrequencyText}{(option.discountText) ? ` (${option.discountText})` : ""}
                          <br />
                          {formatPrice(option.price)} per month
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              )
            }
            <Button
              variant={"contained"}
              color={"primary"}
              style={styles.buyButton}
              type={"submit"}
              disabled={isLoading}
              fullWidth
            >
              {
                isLoading ? (
                  <CircularProgress
                    size={25}
                    color={"secondary"}
                  />
                ) : "Buy now"
              }
            </Button>
          </form>
        </div>
      </section>
      <Link to={PAGE_ROUTES.STORE}>
        <Button
          variant={"text"}
          style={styles.backButton}
        >
          Back to store
        </Button>
      </Link>
    </main>
  );
};

export default Product;

